/**
 * EventBus.ts
 * Type-safe decoupled PubSub event dispatcher for game events, UI updates, and simulation tickers.
 */

type EventHandler<T = any> = (data: T) => void;

export class EventBus {
  private static events: Map<string, Set<EventHandler>> = new Map();

  public static on<T = any>(event: string, handler: EventHandler<T>): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  public static off<T = any>(event: string, handler: EventHandler<T>): void {
    const handlers = this.events.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  public static emit<T = any>(event: string, data?: T): void {
    const handlers = this.events.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (err) {
          console.error(`[EventBus] Error handling event "${event}":`, err);
        }
      });
    }
  }

  public static clear(): void {
    this.events.clear();
  }
}
